import { CheckCircle2, XCircle, Clock, ShieldAlert } from "lucide-react"
import { fmtCompactRelative } from "@/lib/format"
import { cn } from "@/lib/cn"
import type { TestingOverview } from "@/types/agent_testing"

type Props = { overview: TestingOverview }

export function TestingOverviewStrip({ overview }: Props) {
  const passPct  = overview.total_scenarios === 0 ? 0
                 : (overview.passed_count / overview.total_scenarios) * 100
  const failPct  = overview.total_scenarios === 0 ? 0
                 : (overview.failed_count / overview.total_scenarios) * 100
  const erroredPct = overview.total_scenarios === 0 ? 0
                 : (overview.errored_count / overview.total_scenarios) * 100
  const passLabel = overview.overall_pass_rate == null
                 ? "—"
                 : `${(overview.overall_pass_rate * 100).toFixed(1)}%`

  // Conic-gradient donut: green slice for pass, amber for errored,
  // red for failed/never. Math gives us cumulative stops.
  const greenStop  = passPct
  const amberStop  = passPct + erroredPct
  const donutGrad  = `conic-gradient(var(--success) 0 ${greenStop}%, var(--warning) ${greenStop}% ${amberStop}%, var(--error) ${amberStop}% 100%)`

  const failingHere   = overview.failed_count + overview.errored_count
  const needsAttention = failingHere > 0
  const groupsLine = needsAttention
    ? `${failingHere} regression${failingHere === 1 ? "" : "s"} across ${overview.total_scenarios} scenarios`
    : "All scenarios green"

  return (
    <section className="space-y-4">
      <div className="grid gap-4 xl:grid-cols-[300px_minmax(0,1fr)]">
        {/* All latest runs health card */}
        <article className={cn(
          "valet-panel rounded-card p-5",
          needsAttention && "ring-1 ring-brand-redBorder",
        )}>
          <div className="text-[11px] font-medium uppercase tracking-wider text-app-muted">
            All latest run health
          </div>
          <div className="mt-4 flex items-center gap-4">
            <div className={cn(
              "grid h-14 w-14 place-items-center rounded-2xl border text-2xl",
              needsAttention
                ? "border-status-error/40 bg-status-error/10 text-status-error"
                : "border-status-success/40 bg-status-success/10 text-status-success",
            )}>
              <ShieldAlert size={26} />
            </div>
            <div className="min-w-0">
              <div className={cn(
                "text-xl font-semibold leading-tight",
                needsAttention ? "text-status-error" : "text-status-success",
              )}>
                {needsAttention ? "Needs attention" : "Healthy"}
              </div>
              <p className="mt-1 text-sm text-app-muted">{groupsLine}</p>
            </div>
          </div>
          <div className="mt-4 rounded-2xl border border-border bg-white/[.025] p-3 text-sm text-app-muted">
            <span className="font-semibold text-app-text">
              {overview.skill_groups_count} skill{overview.skill_groups_count === 1 ? "" : "s"}
            </span>
            {" "}covered · <span className="font-semibold text-app-text">{overview.pending_promotion_count}</span> draft{overview.pending_promotion_count === 1 ? "" : "s"} pending
          </div>
        </article>

        {/* Stats panel */}
        <article className="valet-panel rounded-card p-0">
          <div className="grid divide-y divide-border sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-6">
            <Stat label="Total scenarios" value={overview.total_scenarios.toString()} sub={`Across ${overview.skill_groups_count} skill group${overview.skill_groups_count === 1 ? "" : "s"}`} />
            <Stat label="Passed" icon={<CheckCircle2 size={14} className="text-status-success" />} value={overview.passed_count.toString()} sub={overview.total_scenarios === 0 ? "—" : `${passPct.toFixed(1)}%`} />
            <Stat label="Failed" icon={<XCircle size={14} className="text-status-error" />} value={overview.failed_count.toString()} sub={overview.total_scenarios === 0 ? "—" : `${failPct.toFixed(1)}%`} valueTone={overview.failed_count > 0 ? "text-status-error" : undefined} />
            <Stat label="Drafts" icon={<Clock size={14} className="text-status-warning" />} value={overview.pending_promotion_count.toString()} sub={overview.skill_groups_count === 0 ? "—" : `${(overview.pending_promotion_count / Math.max(1, overview.skill_groups_count) * 100).toFixed(1)}% of skills`} />
            <Stat label="Latest rehearsal" value={overview.latest_rehearsal_at ? fmtCompactRelative(overview.latest_rehearsal_at) : "Never"} sub={overview.latest_rehearsal_at ?? "—"} subMono />

            {/* Donut cell */}
            <div className="p-5">
              <div className="text-sm text-app-muted">Overall pass rate</div>
              <div className="mt-3 flex items-center gap-3">
                <div className="relative h-12 w-12 rounded-full" style={{ background: donutGrad }} aria-hidden>
                  <div className="absolute inset-[6px] rounded-full bg-app-panel" />
                </div>
                <div className="min-w-0">
                  <div className={cn(
                    "text-2xl font-semibold leading-tight sm:text-3xl",
                    needsAttention ? "text-app-text" : "text-status-success",
                  )}>{passLabel}</div>
                  <div className="text-[11px] text-app-muted">{overview.passed_count} / {overview.total_scenarios} passing</div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>

      {/* Single-row pass/fail bar */}
      <div className="valet-panel rounded-card p-4">
        <div className="mb-3 flex items-center justify-between gap-4 text-xs sm:text-sm">
          <span className="font-semibold text-status-error">{overview.failed_count + overview.errored_count} failed</span>
          <span className="text-app-muted">all latest runs</span>
          <span className="font-semibold text-status-success">{overview.passed_count} passed</span>
        </div>
        <div className="h-3 overflow-hidden rounded-full bg-white/[.06]" role="img" aria-label="Suite health distribution">
          <div className="flex h-full">
            <div className="bg-status-error" style={{ width: `${failPct}%` }} />
            <div className="bg-status-warning" style={{ width: `${erroredPct}%` }} />
            <div className="bg-status-success" style={{ width: `${passPct}%` }} />
          </div>
        </div>
      </div>
    </section>
  )
}

function Stat({
  label, value, sub, icon, valueTone, subMono,
}: {
  label: string
  value: string
  sub: string
  icon?: React.ReactNode
  valueTone?: string
  subMono?: boolean
}) {
  return (
    <div className="p-5">
      <div className="flex items-center gap-2 text-sm text-app-muted">
        {icon} {label}
      </div>
      <div className={cn("mt-3 text-2xl font-semibold sm:text-3xl", valueTone ?? "text-app-text")}>
        {value}
      </div>
      <div className={cn("mt-1 text-[11px] text-app-muted", subMono && "font-mono")}>
        {sub}
      </div>
    </div>
  )
}
